import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {
    materialLoadingSet,
    materialSet,
    selectMaterial,
    selectMaterialType,
    selectThickness,
    thicknessSet,
} from 'components/customer/BTM/store/btmSlice';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {useSearchMaterials} from 'components/customer/BTM/helper/useSearchMaterials';
import {useConfirmationDialog} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {useGetMaterials} from 'components/customer/BTM/helper/useGetMaterials';
import {MiniRadio} from 'shared/components/Forms/MiniRadio';
import {useAppContext} from 'contexts/ApplicationContext';

type Sizes = 'sm' | 'md';
interface ThicknessInterface {
    size: number;
    isSelectedDefault?: boolean;
    readOnly?: boolean;
    imageSize?: Sizes;
}

export const Thickness = ({
    size,
    isSelectedDefault = false,
    readOnly = false,
    imageSize = 'md',
}: ThicknessInterface) => {
    const dispatch = useAppDispatch();
    const type = useAppSelector(selectMaterialType);
    const material = useAppSelector(selectMaterial);
    const {searchMaterials} = useSearchMaterials();
    const {dialog, showDialog} = useConfirmationDialog();
    const {getMaterials} = useGetMaterials();
    const {userProfile} = useAppContext();
    const hideSubstrateName = userProfile?.hideSubstrateName;
    const thickness = useAppSelector(selectThickness);

    const [isSelected, setIsSelected] = useState(isSelectedDefault);

    const selectHandler = useCallback(() => {
        if (readOnly) return;

        const setThickness = async () => {
            dispatch(thicknessSet(size));

            if (type && material) {
                dispatch(materialLoadingSet(true));
                const {materials} = await searchMaterials(
                    type.id,
                    size,
                    1,
                    material.searchName,
                    userProfile?.hideSubstrateName
                );
                const displayName = `${material.name} <em>${material.finish?.name}</em> - ${material.thickness}mm ${material.brand?.name}`;
                if (materials && materials.length > 0) {
                    dispatch(materialSet(materials[0]));
                } else {
                    // If the material is not available in the selected thickness
                    const materialWithDifferentNames = await getMaterials(
                        type.id,
                        size
                    );

                    if (
                        materialWithDifferentNames &&
                        materialWithDifferentNames.length > 0
                    ) {
                        dispatch(materialSet(materialWithDifferentNames[0]));
                    } else {
                        dispatch(materialSet(undefined));
                    }
                    showDialog({
                        title: 'Benchtop Material',
                        message: (
                            <>
                                Material{' '}
                                <strong>
                                    {hideSubstrateName
                                        ? parseHtmlString(displayName)
                                        : parseHtmlString(
                                              material?.displayName
                                          )}
                                </strong>{' '}
                                is not available in thickness{' '}
                                <strong>{size}mm</strong>.
                                <br />
                                <br />
                                Please Browse Colours or Search to select a
                                different material.
                            </>
                        ),
                        hideYesButton: true,
                        hideNoButton: true,
                        buttons: [
                            {
                                show: true,
                                name: 'Okay',
                            },
                        ],
                    });
                }
                dispatch(materialLoadingSet(false));
            }
        };

        void setThickness();
    }, [
        thicknessSet,
        materialLoadingSet,
        materialSet,
        searchMaterials,
        type,
        material,
        size,
        getMaterials,
        userProfile,
        readOnly,
    ]);

    const className = useMemo(
        () => (isSelected ? 'selected' : ''),
        [isSelected]
    );

    useEffect(() => {
        if (!readOnly) {
            const currentThickness = material?.thickness || thickness;
            setIsSelected(currentThickness == size);
        }
    }, [size, thickness, material?.thickness, readOnly]);

    return (
        <>
            {readOnly ? null : dialog}
            <ThicknessSelector className={className} onClick={selectHandler}>
                <Container $size={imageSize}>
                    <Shape
                        className="shape"
                        $size={size / 2}
                        $imageSize={imageSize}
                    />
                    <Size className="size" $size={imageSize}>
                        <Number className="number">{size}</Number>
                    </Size>
                </Container>

                {readOnly ? null : (
                    <MiniRadio $checked={isSelected}>
                        {size}mm
                        <input
                            type="radio"
                            id={`boolean_${size}_yes`}
                            name={`thickness_${size}`}
                            checked={isSelected}
                            readOnly={true}
                        />
                    </MiniRadio>
                )}
            </ThicknessSelector>
        </>
    );
};

const Container = styled.div<{$size: Sizes}>`
    width: ${({$size}) => ($size == 'md' ? '130px' : '80px')};
    height: ${({$size}) => ($size == 'md' ? '130px' : '80px')};
    border-radius: 15px;
    position: relative;
    overflow: hidden;
    border: 1px solid rgb(var(--primary_colour));
    margin-bottom: ${({$size}) => ($size == 'md' ? '10px' : '0')};
    background: white;
`;

const Shape = styled.div<{$size: number; $imageSize: Sizes}>`
    background: #b9b9b9;
    width: 200px;
    height: 200px;
    transform: rotate(45deg);
    position: absolute;
    left: -${({$imageSize}) => ($imageSize == 'md' ? '60px' : '75px')};
    top: -${({$imageSize}) => ($imageSize == 'md' ? '170px' : '210px')};
    transition: background 500ms;

    &:before {
        content: '';
        width: 200px;
        height: ${({$size}) => $size}px;
        background: #b9b9b9;
        position: absolute;
        bottom: -${({$size}) => $size + 2}px;
        transform: skew(45deg);
        left: ${({$size}) => $size * 0.55}px;
        transition: background 500ms;
    }

    &:after {
        content: '';
        width: ${({$size}) => $size}px;
        height: 200px;
        background: #b9b9b9;
        position: absolute;
        right: -${({$size}) => $size + 2}px;
        transform: skew(0deg, 45deg);
        top: ${({$size}) => $size * 0.55}px;
        transition: background 500ms;
    }
`;

const Size = styled.div<{$size: Sizes}>`
    position: absolute;
    bottom: ${({$size}) => ($size == 'md' ? '15px' : '5px')};
    right: ${({$size}) => ($size == 'md' ? '15px' : '5px')};
    font-weight: bold;
    font-size: ${({$size}) => ($size == 'md' ? '1.2rem' : '.85rem')};
    border-left: 1px solid #b9b9b9;
    padding-left: 5px;
    transition: border-color 500ms;
`;

const Number = styled.div`
    position: relative;
    color: #b9b9b9;
    transition: color 500ms;

    &:before {
        position: absolute;
        content: '';
        width: 20px;
        height: 1px;
        top: 0;
        left: -15px;
        background: #b9b9b9;
        transition: background 500ms;
    }

    &:after {
        position: absolute;
        content: '';
        width: 20px;
        height: 1px;
        bottom: 0;
        left: -15px;
        background: #b9b9b9;
        transition: background 500ms;
    }
`;

const ThicknessSelector = styled.div`
    cursor: pointer;
    text-align: center;
    margin-right: 15px;

    &:last-of-type {
        margin-right: 0;
    }

    .cbc-radio {
        margin-top: 5px;
    }

    .cbc-radio .form-check label::before {
        left: 30px !important;
    }

    .cbc-radio .form-check label::after {
        left: 25px;
    }

    &.selected,
    :hover {
        .shape {
            background: rgb(var(--primary_colour));
        }

        .shape::before {
            background: rgb(var(--primary_colour));
        }

        .shape::after {
            background: rgb(var(--primary_colour));
        }

        .size {
            border-color: rgb(var(--primary_colour));

            .number {
                color: rgb(var(--primary_colour));
            }

            .number::before {
                background: rgb(var(--primary_colour));
            }

            .number::after {
                background: rgb(var(--primary_colour));
            }
        }
    }
`;
